// Phase RV-6a — FLEX builder pure helpers.
//
// Source: ROLLOVER Intelligence Enhancement v1.0 §11.2 (FLEX Builder)
//         + §11.6 N13/N14/N18 + v1 N-F01/F02/F03/F05.
//
// All functions here are pure and I/O-free. They mirror the SQL
// validation inside `public.flex_lock_slip` so the UI can compute
// lockability without round-tripping.

import type { BoardCandidate } from "./upcomingBoardHelpers.ts";

export interface FlexLeg {
  candidate_id: string;
  fixture_id: string | null;
  match_name: string;
  tier: BoardCandidate["tier"];
  market: string | null;
  pick: string | null;
  odds: number | null;
  confidence_score: number | null;
  kickoff_at: string | null;
  pool_tag: BoardCandidate["pool_tag"];
  window_label: BoardCandidate["window_label"];
}

/** N-F02 minimum legs. */
export const FLEX_MIN_LEGS = 2;

/**
 * N-F01 / N13 — only SELECTED and EVALUATED candidates are eligible.
 * REJECTED ('assessed') candidates cannot be added.
 */
export function eligibleFromCandidates(
  candidates: readonly BoardCandidate[],
): BoardCandidate[] {
  return candidates.filter(
    (c) => c.pool_tag === "selected" || c.pool_tag === "evaluated",
  );
}

/**
 * Promote a BoardCandidate to a FlexLeg shape. Pure.
 */
export function toFlexLeg(c: BoardCandidate): FlexLeg {
  return {
    candidate_id: c.candidate_id,
    fixture_id: c.fixture_id,
    match_name: c.match_name,
    tier: c.tier,
    market: c.market,
    pick: c.pick,
    odds: c.odds,
    confidence_score: c.confidence_score,
    kickoff_at: c.kickoff_at,
    pool_tag: c.pool_tag,
    window_label: c.window_label,
  };
}

/** Multiplicative combined odds. No rounding (caller rounds for display). */
export function combinedOddsOf(legs: readonly FlexLeg[]): number {
  let p = 1;
  for (const l of legs) {
    if (l.odds === null || !Number.isFinite(l.odds)) continue;
    p *= l.odds;
  }
  return p;
}

/**
 * N14 / N-F02 — no two legs share a fixture_id.
 * Returns the first conflict (if any).
 */
export function validateNoSameFixture(
  legs: readonly FlexLeg[],
): { ok: true } | { ok: false; conflict: { a: FlexLeg; b: FlexLeg } } {
  for (let i = 0; i < legs.length; i++) {
    const a = legs[i]!;
    if (a.fixture_id === null) continue;
    for (let j = i + 1; j < legs.length; j++) {
      const b = legs[j]!;
      if (b.fixture_id === a.fixture_id) {
        return { ok: false, conflict: { a, b } };
      }
    }
  }
  return { ok: true };
}

/**
 * N-F03 — pairwise contradiction check.
 *
 * Mirrors the SQL helper. Inline rules (kept narrow / honest):
 *   - Same fixture: "Under 2.5" + "BTTS Yes" → contradict
 *   - Same fixture: "Over X" + "Under X" same line → contradict
 * Cross-fixture legs are never contradictory at this layer
 * (correlation is N14 / fixture uniqueness).
 */
export function validateContradictions(
  legs: readonly FlexLeg[],
): { ok: true } | { ok: false; conflicts: { a: FlexLeg; b: FlexLeg; reason: string }[] } {
  const conflicts: { a: FlexLeg; b: FlexLeg; reason: string }[] = [];
  for (let i = 0; i < legs.length; i++) {
    const a = legs[i]!;
    for (let j = i + 1; j < legs.length; j++) {
      const b = legs[j]!;
      if (a.fixture_id === null || b.fixture_id === null) continue;
      if (a.fixture_id !== b.fixture_id) continue;
      const am = (a.market ?? "").toLowerCase();
      const bm = (b.market ?? "").toLowerCase();
      const ap = (a.pick ?? "").toLowerCase();
      const bp = (b.pick ?? "").toLowerCase();
      if (
        (am.includes("under 2.5") && bm.includes("btts") && bp.includes("yes")) ||
        (bm.includes("under 2.5") && am.includes("btts") && ap.includes("yes"))
      ) {
        conflicts.push({ a, b, reason: "under_2_5_plus_btts_yes" });
        continue;
      }
      if (am === bm && am.length > 0 && am.includes("over") === false && bm.includes("over") === false) {
        // identical market+different pick on same fixture
        if (ap !== bp && ap.length > 0 && bp.length > 0) {
          conflicts.push({ a, b, reason: "same_market_conflicting_pick" });
        }
      }
    }
  }
  return conflicts.length === 0 ? { ok: true } : { ok: false, conflicts };
}

/**
 * N-F05 — first leg kickoff must be in the future.
 * Returns the earliest kickoff for display.
 */
export function canLock(
  legs: readonly FlexLeg[],
  now: Date = new Date(),
): { ok: true; firstKickoff: Date | null } | { ok: false; reason: string } {
  if (legs.length < FLEX_MIN_LEGS) {
    return { ok: false, reason: "min_two_legs" };
  }
  let first: Date | null = null;
  for (const l of legs) {
    if (l.kickoff_at === null) continue;
    const t = new Date(l.kickoff_at);
    if (Number.isNaN(t.getTime())) continue;
    if (first === null || t.getTime() < first.getTime()) first = t;
  }
  if (first !== null && first.getTime() <= now.getTime()) {
    return { ok: false, reason: "window_expired" };
  }
  const sameFix = validateNoSameFixture(legs);
  if (!sameFix.ok) return { ok: false, reason: "same_fixture" };
  const contr = validateContradictions(legs);
  if (!contr.ok) return { ok: false, reason: "contradiction" };
  for (const l of legs) {
    if (l.pool_tag !== "selected" && l.pool_tag !== "evaluated") {
      return { ok: false, reason: "pool_tag_ineligible" };
    }
    if (l.odds === null) return { ok: false, reason: "odds_missing" };
  }
  return { ok: true, firstKickoff: first };
}
